<template>
  <div class="card">
    <PersonPictureSet
      class="profile-picture-new w-full h-auto block max-w-[280px] mx-auto"
      height="280"
      width="280"
      :person="person"
      :alt="person.name"
      not-lazy
      fetch-priority="high"
      @error="handlePictureError"
    />
    <RatingIndicator :score="person.score" class="rating-stars" />
    <div class="person-name">
      {{ person.name }}
    </div>
    <p
      v-if="topRankedProfessions?.length"
      class="font-bold text-sm align-middle"
    >
      <IconComponent icon="briefcase" class="inline mr-1" />
      <template v-for="(profession, index) in topRankedProfessions">
        {{ profession.name }}
        {{ index !== topRankedProfessions.length - 1 ? " · " : "" }}
      </template>
    </p>
    <div class="flex flex-col gap-2 items-center text-center">
      <PersonStats :person="person" />
      <p class="text-sm">
        {{ $t("profileCard.lastSeen", { timeAgo: dateDifferenceToNow }) }}
      </p>

      <template v-if="isOnlineProfession">
        <span class="flex flex-row items-center gap-1 text-sm">
          <IconComponent icon="globe" class="inline w-4 text-primary-light" />
          {{ $t("personProfile.location.onlineCoverage.cardOnline") }}
        </span>
        <span
          v-if="person.city?.name"
          class="flex flex-row items-center gap-1 text-sm"
        >
          <IconComponent
            icon="map-marker-alt"
            class="inline w-4 text-red-500"
          />
          {{
            $t("personProfile.location.onlineCoverage.cardInPerson", {
              city: person.city.name,
            })
          }}
        </span>
      </template>
      <p v-else-if="!city?.name" class="text-sm">
        <IconComponent icon="map-marker-alt" class="inline mr-2 text-red-500" />
        <template v-if="person.goToHouse">
          <template v-if="goToWholeCountry">
            {{ $t("personProfile.location.everywhere") }}
          </template>
          <template v-else>
            <i18n path="personProfile.location.goToHouse">
              <template #city>
                <b>{{ person.city?.name }}</b>
              </template>
              <template #km>
                <b>
                  {{ person.goToHouseKm }}
                </b>
              </template>
            </i18n>
          </template>
        </template>
        <template v-else>
          <i18n path="personProfile.location.inCity">
            <template #city>
              <b>{{ person.city?.name }}</b>
            </template>
          </i18n>
        </template>
      </p>
      <p v-else-if="city?.name">
        <IconComponent icon="check" class="text-success inline mr-2" />
        <b>
          <span>{{ $t("workThere.text", { cityName: city.name }) }}</span>
        </b>
      </p>

      <p v-if="person.phoneNumber?.number">
        <IconComponent icon="phone-alt" class="inline mr-2" />
        {{ $helpers.formatPhoneNumber(person.phoneNumber) }}
      </p>
      <JoszakiButton
        type="info"
        class="w-full py-2 flex-col"
        @click="$emit('requestTender')"
      >
        <p class="font-bold !mb-0">{{ $t("profileCard.requestTender") }}</p>
        <p>
          <small>({{ $t("profileCard.quickOfferSubtitle") }})</small>
        </p>
      </JoszakiButton>
    </div>
  </div>
</template>

<script>
import { DateTime } from "luxon";
import { mapState } from "pinia";
import PersonStats from "./PersonStats.vue";
import PersonPictureSet from "~/components/PersonPictureSet.vue";
import RatingIndicator from "~/components/_refactored/rating/RatingIndicator.vue";
import { differenceToNow } from "~/helpers/date";
import { useProfessionalStore } from "~/stores/professional";
import { isOnlineProfessionEnabled } from "~/experiments/onlineProfession";

export default {
  components: {
    PersonStats,
    PersonPictureSet,
    RatingIndicator,
  },
  props: {
    person: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return {
      professionalStore: useProfessionalStore(),
    };
  },
  computed: {
    ...mapState(useProfessionalStore, {
      rankedProfessions: "rankedProfessions",
      city: "city",
      profession: "profession",
    }),
    isOnlineProfession() {
      if (!isOnlineProfessionEnabled(this.$ab)) {
        return false;
      }
      if (this.profession) {
        return !!this.profession.online;
      }
      const professions = this.person?.professions ?? [];
      return professions.length > 0 && professions.every((p) => p.online);
    },
    dateDifferenceToNow() {
      if (!this.person?.lastLogin) {
        return null;
      }
      const lastHere = DateTime.fromISO(this.person.lastLogin).diff(
        DateTime.now(),
        "days"
      );

      // if was here in the past 5 years return null
      if (lastHere > 5 * 356) {
        return null;
      }

      return differenceToNow(this, this.person?.lastLogin);
    },
    goToWholeCountry() {
      return this.person?.goToHouseKm > 500;
    },
    topRankedProfessions() {
      return this.rankedProfessions.slice(0, 3);
    },
  },
  methods: {
    handlePictureError() {
      this.professionalStore.pictureError();
    },
  },
};
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-flow: column;
  border-radius: 1rem;
  overflow: hidden;
  padding: 1rem;
  // Bulma's `.card` previously provided the white background + shadow.
  background-color: #fff;
  box-shadow: 0 0.5em 1em -0.125em rgba(10, 10, 10, 0.1),
    0 0 0 1px rgba(10, 10, 10, 0.02);
}

.profile-picture-new {
  -webkit-clip-path: circle(50% at 50% 50%);
  clip-path: circle(50% at 50% 50%);
}

.rating-stars {
  padding-bottom: 5px;
}
.person-name {
  font-weight: 800;
  text-align: left;
  font-family: nunito, sans-serif;
  font-size: 1.1em;
  margin-bottom: 1rem;
}

.user-social-box {
  display: table;
  width: 100%;
  border-top: #e4e4e4;
  border-bottom: #e4e4e4;
  position: relative;
  font-size: 1rem;
  margin-top: 1rem;

  &:before {
    background: #e4e4e4;
    position: absolute;
    content: "";
    display: block;
    left: 50%;
    width: 1px;
    height: 100%;
  }
}

.facebook-box {
  display: table-cell;
  vertical-align: middle;
  height: 1rem;
  text-align: center;
  width: 50%;
}

.has-smaller-text {
  font-size: 14px !important;
}
</style>
